import { createFileRoute, Link, useRouter } from "@tanstack/react-router"
import { useState, useEffect, type ReactNode } from "react"
import { useMutation } from "@tanstack/react-query"
import { Layout } from "@/components/layout"
import { BackLink } from "@/components/back-link"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { ResponsiveDialog } from "@/components/responsive-dialog"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { supabase } from "@/lib/supabase"
import { deleteUser } from "@/lib/api/user"
import { useIsDesktop } from "@/hooks/use-is-desktop"
import { FOOTER_LINKS } from "@/components/layout/footer"
import { AdminControls } from "@/components/layout/admin-controls"

const rowClassName =
  "flex min-h-12 w-full items-center gap-2 px-4 text-left text-[17px] text-[var(--heading)]"

const GroupedList = ({ children }: { children: ReactNode }) => (
  <ul className="divide-y divide-[var(--surface-hairline)] overflow-hidden rounded-xl bg-card text-[17px] text-[var(--heading)]">
    {children}
  </ul>
)

const Group = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="flex flex-col gap-2">
    <h2 className="pl-4 text-[13px] text-muted-foreground uppercase">
      {title}
    </h2>
    <GroupedList>{children}</GroupedList>
  </section>
)

const Chevron = () => (
  <svg
    width="8"
    height="14"
    viewBox="0 0 8 14"
    aria-hidden="true"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="shrink-0 text-muted-foreground/50"
  >
    <path d="M1 1l6 6-6 6" />
  </svg>
)

function Account() {
  const router = useRouter()
  const isDesktop = useIsDesktop()
  const [editing, setEditing] = useState<"name" | "email" | null>(null)

  const [displayName, setDisplayName] = useState("")
  const [email, setEmail] = useState("")
  const [nameInput, setNameInput] = useState("")
  const [emailInput, setEmailInput] = useState("")
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const [nameSuccess, setNameSuccess] = useState(false)
  const [emailSuccess, setEmailSuccess] = useState(false)

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const user = data.user
      if (!user) return
      const name = user.user_metadata?.display_name || user.email || ""
      setDisplayName(name)
      setNameInput(name)
      setEmail(user.email || "")
      setEmailInput(user.email || "")
    })
  }, [])

  const {
    mutate: saveName,
    isPending: savingName,
    error: nameError,
  } = useMutation({
    mutationFn: async (name: string) => {
      const { error } = await supabase.auth.updateUser({
        data: { display_name: name },
      })
      if (error) throw error
    },
    onSuccess: (_, name) => {
      setDisplayName(name)
      setNameSuccess(true)
      setTimeout(() => setNameSuccess(false), 3000)
    },
  })

  const {
    mutate: saveEmail,
    isPending: savingEmail,
    error: emailError,
  } = useMutation({
    mutationFn: async (newEmail: string) => {
      const { error } = await supabase.auth.updateUser({ email: newEmail })
      if (error) throw error
    },
    onSuccess: () => {
      setEmailSuccess(true)
      setTimeout(() => setEmailSuccess(false), 5000)
    },
  })

  const {
    mutate: confirmDelete,
    isPending: deleting,
    error: deleteError,
  } = useMutation({
    mutationFn: deleteUser,
    onSuccess: async () => {
      await supabase.auth.signOut()
      router.navigate({ to: "/auth/signin" })
    },
  })

  const startEditing = (field: "name" | "email") => {
    setNameInput(displayName)
    setEmailInput(email)
    setEditing(field)
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    window.location.href = "/auth/signin"
  }

  const deleteDialog = (
    <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete your account?</DialogTitle>
          <DialogDescription>
            This will permanently delete your account and all your data —
            reading progress, knowledge items, and story history. This action
            cannot be undone.
          </DialogDescription>
        </DialogHeader>
        {deleteError && (
          <p className="text-xs text-destructive">{deleteError.message}</p>
        )}
        <DialogFooter showCloseButton>
          <Button
            variant="destructive"
            onClick={() => confirmDelete()}
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "Delete account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  if (!isDesktop) {
    return (
      <Layout title="Account">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <Group title="Profile">
              <li>
                <button
                  type="button"
                  className={rowClassName}
                  onClick={() => startEditing("name")}
                >
                  <span className="flex-1">Display name</span>
                  <span className="max-w-[50%] truncate text-muted-foreground">
                    {displayName}
                  </span>
                  <Chevron />
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className={rowClassName}
                  onClick={() => startEditing("email")}
                >
                  <span className="flex-1">Email</span>
                  <span className="max-w-[60%] truncate text-muted-foreground">
                    {email}
                  </span>
                  <Chevron />
                </button>
              </li>
            </Group>
            {nameSuccess && (
              <p className="pl-4 text-xs text-success-deep">Name updated.</p>
            )}
            {emailSuccess && (
              <p className="pl-4 text-xs text-success-deep">
                Check your new email for a confirmation link.
              </p>
            )}
          </div>

          <div className="mb-6">
            <GroupedList>
              <li>
                <button
                  type="button"
                  className="min-h-12 w-full text-primary"
                  onClick={handleSignOut}
                >
                  Sign out
                </button>
              </li>
            </GroupedList>
          </div>

          <Group title="Support">
            {FOOTER_LINKS.map((link) => (
              <li key={link.label}>
                {link.to ? (
                  <Link to={link.to} className={rowClassName}>
                    <span className="flex-1">{link.label}</span>
                    <Chevron />
                  </Link>
                ) : (
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={rowClassName}
                  >
                    <span className="flex-1">{link.label}</span>
                    <Chevron />
                  </a>
                )}
              </li>
            ))}
          </Group>

          <AdminControls />

          {/* Kept apart from Sign out so it isn't hit by mistake. */}
          <button
            type="button"
            className="min-h-12 w-full rounded-xl bg-(--surface-card) text-[17px] text-[var(--heading)]"
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete account
          </button>
        </div>

        <ResponsiveDialog
          open={editing === "name"}
          onOpenChange={(open) => !open && setEditing(null)}
          title="Display name"
          footer={
            <Button
              onClick={() =>
                saveName(nameInput, { onSuccess: () => setEditing(null) })
              }
              disabled={savingName || nameInput === displayName}
            >
              {savingName ? "Saving…" : "Save"}
            </Button>
          }
        >
          <div className="space-y-2">
            <Input
              aria-label="Display name"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder="Your name"
            />
            <p className="text-xs text-muted-foreground">
              This is how you appear across the app.
            </p>
            {nameError && (
              <p className="text-xs text-destructive">{nameError.message}</p>
            )}
          </div>
        </ResponsiveDialog>

        <ResponsiveDialog
          open={editing === "email"}
          onOpenChange={(open) => !open && setEditing(null)}
          title="Email"
          footer={
            <Button
              onClick={() =>
                saveEmail(emailInput, { onSuccess: () => setEditing(null) })
              }
              disabled={savingEmail || emailInput === email || !emailInput}
            >
              {savingEmail ? "Saving…" : "Save"}
            </Button>
          }
        >
          <div className="space-y-2">
            <Input
              aria-label="Email"
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="you@example.com"
            />
            <p className="text-xs text-muted-foreground">
              You'll receive a confirmation email before the change takes
              effect.
            </p>
            {emailError && (
              <p className="text-xs text-destructive">{emailError.message}</p>
            )}
          </div>
        </ResponsiveDialog>

        {deleteDialog}
      </Layout>
    )
  }

  return (
    <Layout>
      <BackLink to="/stories" className="mb-8" />

      <div className="max-w-lg space-y-10">
        <div>
          <h1 className="text-xl font-semibold">Account</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your account settings.
          </p>
        </div>

        {/* Change name */}
        <section className="space-y-4">
          <div>
            <h2 className="text-sm font-medium">Display Name</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              This is how you appear across the app.
            </p>
          </div>
          <div className="flex gap-2">
            <Input
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder="Your name"
              className="max-w-xs"
            />
            <Button
              onClick={() => saveName(nameInput)}
              disabled={savingName || nameInput === displayName}
              size="sm"
            >
              {savingName ? "Saving…" : "Save"}
            </Button>
          </div>
          {nameSuccess && (
            <p className="text-xs text-success-deep">Name updated.</p>
          )}
          {nameError && (
            <p className="text-xs text-destructive">{nameError.message}</p>
          )}
        </section>

        {/* Change email */}
        <section className="space-y-4">
          <div>
            <h2 className="text-sm font-medium">Email Address</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              You'll receive a confirmation email before the change takes
              effect.
            </p>
          </div>
          <div className="flex gap-2">
            <Input
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="you@example.com"
              className="max-w-xs"
            />
            <Button
              onClick={() => saveEmail(emailInput)}
              disabled={savingEmail || emailInput === email || !emailInput}
              size="sm"
            >
              {savingEmail ? "Saving…" : "Save"}
            </Button>
          </div>
          {emailSuccess && (
            <p className="text-xs text-success-deep">
              Check your new email for a confirmation link.
            </p>
          )}
          {emailError && (
            <p className="text-xs text-destructive">{emailError.message}</p>
          )}
        </section>

        {/* Delete account */}
        <section className="space-y-4 rounded-2xl border border-destructive/20 p-5">
          <div>
            <h2 className="text-sm font-medium text-destructive">
              Delete Account
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Permanently delete your account and all associated data. This
              cannot be undone.
            </p>
          </div>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete account
          </Button>
        </section>
      </div>

      {deleteDialog}
    </Layout>
  )
}

export const Route = createFileRoute("/_authenticated/account")({
  component: Account,
})
